import { TextField, DateField, useRecordContext, Labeled, RecordContextProvider } from "react-admin";

import {
  Grid,
  Box,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableContainer,
  Collapse,
  IconButton,
  Typography,
} from "@mui/material";

import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";

import { settings } from "app";
import { SectionTitle } from "features/SectionTitle";
import { useMediaQuery } from "@mui/material";
import { useState, Fragment } from "react";
const arrowStyle = { fontSize: 28, background: "#6A26C8", borderRadius: "50%", color: "#fff", p: 0.1 };
const TransactionsExpand = ({ transactions = [] }) => {
  const isMobile = useMediaQuery(theme => theme.breakpoints.down("md"));

  if (!transactions?.length) {
    return <Typography variant="body2">Няма трансакции</Typography>;
  }

  return (
    <Box
      flexDirection="column"
      gap={2}
    >
      {transactions.map(transaction => (
        <Box
          key={transaction.id}
          display="flex"
          flexDirection={isMobile ? "column" : "row"}
          gap={isMobile ? 1 : 6}
          textAlign={"start"}
          sx={{
            border: "1px solid #e0e0e0",
            borderRadius: 1,
            p: 2,
            boxShadow: 1,
            backgroundColor: "#e5e3e333",
          }}
        >
          <Labeled label="Дата на плащане">
            {transaction.paymentTimestamp ? (
              <DateField
                record={transaction}
                source="paymentTimestamp"
                locales="bg-BG"
                showTime
              />
            ) : (
              <Typography variant="body2">-</Typography>
            )}
          </Labeled>

          <Labeled label="Вид плащане">
            <Typography variant="body2">{transaction.providerName || "-"}</Typography>
          </Labeled>

          <Labeled label="Сума">
            <Typography variant="body2">{transaction.amountFormatted || transaction.amount || "-"}</Typography>
          </Labeled>

          <Labeled label="Валута">
            <Typography variant="body2">{transaction.currency || "-"}</Typography>
          </Labeled>

          <Labeled label="Описание">
            <Typography variant="body2">{transaction.description || "-"}</Typography>
          </Labeled>
          <Labeled label="Статус">
            <Typography variant="body2">{transaction.status || "-"}</Typography>
          </Labeled>
        </Box>
      ))}
    </Box>
  );
};

const MobileDebtsList = () => {
  const record = useRecordContext();

  const [expanded, setExpanded] = useState({});

  if (!record?.debts?.length) return null;

  const toggleExpand = debtId => {
    setExpanded(prev => ({
      ...prev,
      [debtId]: !prev[debtId],
    }));
  };

  return (
    <Box
      display="flex"
      flexDirection="column"
      gap={2}
    >
      {record.debts.map((item, index) => (
        <RecordContextProvider
          key={index}
          value={item}
        >
          <Box
            display="flex"
            flexDirection="column"
            sx={{
              border: "1px solid #e0e0e0",
              borderRadius: 2,
              p: 2,
              boxShadow: 1,
              backgroundColor: "#fff",
            }}
          >
            <Labeled label="Сума (EUR)">
              <TextField source="amountFormatted" />
            </Labeled>

            <Labeled label="Кампания">
              <TextField source="typeName" />
            </Labeled>

            <Labeled label="Дата на създаване">
              <DateField
                locales="bg-BG"
                source="createdAt"
                showTime
              />
            </Labeled>

            <Labeled label="Статус">
              <TextField source="statusText" />
            </Labeled>

            <Box
              mt={2}
              display="flex"
              justifyContent="flex-end"
            >
              <IconButton onClick={() => toggleExpand(item.id)}>
                {expanded[item.id] ? (
                  <KeyboardArrowUpIcon sx={arrowStyle} />
                ) : (
                  <KeyboardArrowDownIcon sx={arrowStyle} />
                )}
              </IconButton>
            </Box>

            <Collapse in={expanded[item.id]}>
              <Box mt={2}>
                <TransactionsExpand transactions={item.transactions} />
              </Box>
            </Collapse>
          </Box>
        </RecordContextProvider>
      ))}
    </Box>
  );
};

const DesktopDebtTable = () => {
  const record = useRecordContext();

  const [expanded, setExpanded] = useState({});

  if (!record?.debts?.length) return null;

  const toggleExpand = debtId => {
    setExpanded(prev => ({
      ...prev,
      [debtId]: !prev[debtId],
    }));
  };

  return (
    <TableContainer sx={settings.style.customDatagridStyle}>
      <Table>
        <TableHead>
          <TableRow>
            <TableCell width={60}></TableCell>

            <TableCell>Сума (EUR)</TableCell>
            <TableCell>Кампания</TableCell>
            <TableCell>Дата на създаване</TableCell>
            <TableCell>Статус</TableCell>
          </TableRow>
        </TableHead>

        <TableBody>
          {record.debts.map((item, index) => (
            <Fragment key={index}>
              <RecordContextProvider value={item}>
                <TableRow>
                  <TableCell>
                    <IconButton onClick={() => toggleExpand(item.id)}>
                      {expanded[item.id] ? (
                        <KeyboardArrowUpIcon sx={arrowStyle} />
                      ) : (
                        <KeyboardArrowDownIcon sx={arrowStyle} />
                      )}
                    </IconButton>
                  </TableCell>

                  <TableCell>
                    <TextField source="amountFormatted" />
                  </TableCell>

                  <TableCell>
                    <TextField source="typeName" />
                  </TableCell>

                  <TableCell>
                    <DateField
                      locales="bg-BG"
                      source="createdAt"
                      showTime
                    />
                  </TableCell>

                  <TableCell>
                    <TextField source="statusText" />
                  </TableCell>
                </TableRow>
              </RecordContextProvider>

              <TableRow>
                <TableCell
                  colSpan={6}
                  sx={{
                    paddingTop: 0,
                    paddingBottom: 0,
                    borderBottom: 0,
                  }}
                >
                  <Collapse
                    in={expanded[item.id]}
                    timeout="auto"
                    unmountOnExit
                  >
                    <Box m={2}>
                      <SectionTitle paddingY={2} title="Трансакции" />

                      <TransactionsExpand transactions={item.transactions} />
                    </Box>
                  </Collapse>
                </TableCell>
              </TableRow>
            </Fragment>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

const DebtsSection = () => {
  const isMobile = useMediaQuery(theme => theme.breakpoints.down("md"));

  return (
    <Grid
      container
      spacing={2}
      sx={isMobile ? settings.style.shadowBoxMobile : settings.style.shadowBox}
    >
      <Grid
        item
        xs={12}
      >
        <SectionTitle
          title="Задължения"
          paddingY={0}
        />
      </Grid>

      <Grid
        item
        xs={12}
      >
        {isMobile ? <MobileDebtsList /> : <DesktopDebtTable />}
      </Grid>
    </Grid>
  );
};

export default DebtsSection;
